import type { CommonAction } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema } from "../../types";
import { executeAction } from "../../utils";
import { mockAction } from "./mock";
import { rpaAction } from "./rpa";

/**
 * 内容声明标注类型，对应发布体 `tagInfo.tagType`
 *
 * 取值来自 `post/finder_get_object_tag_list` 实测响应：
 * 0=无需标注、1=含AI生成内容、2=内容包含营销广告、3=内容为虚构剧情仅供娱乐、
 * 5=内容为自行拍摄、7=内容为转载、8=个人观点仅供参考
 */
export const ShipinhaoDeclareTagTypes = [0, 1, 2, 3, 5, 7, 8] as const;

export const ShipinhaoPublishVideoParamsSchema = ActionCommonParamsSchema.extend({
    /** 本地视频路径（MP4/H.264，时长 ≤ 8 小时，大小 ≤ 20GB） */
    videoPath: z.string().min(1),
    /**
     * 视频元数据，由调用方在浏览器侧采集后传入
     *
     * `<video>` 加载后取 `videoWidth` / `videoHeight` / `duration`，`fileSize` 取 `File.size`。
     * 传入的宽高必须是**显示尺寸**（浏览器已应用旋转），SDK 直接用于发布体与转码请求，
     * 不再做旋转换算 —— 竖屏视频传 tkhd 里的横屏尺寸会导致转码尺寸错误。
     *
     * 编码格式（H.264 校验用）不在此列：浏览器无 API 可取，由 SDK 从文件分段读取。
     */
    videoMetadata: z.object({
        /** 时长（秒，可为小数），对应 media[].videoPlayLen 与 clipOriginVideoInfo.duration */
        duration: z.number().positive(),
        /** 显示宽度（px），对应 post_clip_video 的 targetWidth */
        width: z.number().int().positive(),
        /** 显示高度（px），对应 post_clip_video 的 targetHeight */
        height: z.number().int().positive(),
        /** 文件字节数，用于 20GB 上限校验 */
        fileSize: z.number().int().positive(),
        /** 视频文件路径（本地绝对路径） */
        path: z.string().min(1).optional(),
        /** 视频文件名（含扩展名） */
        fileName: z.string().min(1),
    }),
    /**
     * 横屏封面图片路径（JPEG/PNG），对应 media[].thumbUrl / fullThumbUrl
     *
     * 不传 verticalCoverPath 时，竖屏封面字段也复用此图。
     */
    coverPath: z.string().min(1),
    /**
     * 竖屏封面图片路径（可选，JPEG/PNG），对应 media[].coverUrl / fullCoverUrl / shareCoverUrl
     *
     * 不传则退回 coverPath，此时五个封面字段同图（实测浏览器只选一张封面时就是这个表现）。
     */
    verticalCoverPath: z.string().min(1).optional(),
    /** 发布文案，对应 objectDesc.description；话题与 @用户会自动拼接到文案末尾 */
    description: z.string(),
    /** 短标题（可选，对应 objectDesc.shortTitle[0].shortTitle，填了需在 6-16 个字符之间） */
    title: z.string().optional(),
    /** 定时发布时间戳（可选，10 位秒级，对应 effectiveTime） */
    scheduledTime: z.number().int().positive().optional(),
    /** 是否立即发布（可选，对应顶层 isImmediatelyPublish，默认 false） */
    isImmediatelyPublish: z.boolean().optional(),
    /** 话题（可选，不带 # 号，对应顶层 topics 与 objectDesc.topic.finderTopicInfo） */
    topics: z.array(z.string()).optional(),
    /** @提及的用户（可选，对应 objectDesc.mentionedUser） */
    mentionedUsers: z
        .array(
            z.object({
                /** 昵称，不带 @ 号 */
                nickname: z.string(),
            }),
        )
        .optional(),
    /** 合集（可选，对应 objectDesc.topic.collectionId / collectionName） */
    collection: z
        .object({
            /** 合集 id，形如 event/UzFfAgtgekIEAQ... */
            collectionId: z.string(),
            collectionName: z.string(),
        })
        .optional(),
    /** 活动（可选，对应 objectDesc.event） */
    event: z
        .object({
            /** 活动 id，形如 event/UzFfAgtgekIEAQ... */
            eventTopicId: z.string(),
            eventName: z.string(),
            eventCreatorNickname: z.string().optional(),
        })
        .optional(),
    /** 扩展阅读链接（可选，对应 objectDesc.extReading） */
    link: z
        .object({
            link: z.string(),
            title: z.string(),
            /** 1=公众号文章 2=红包封面 */
            urlType: z.number().int().default(1),
        })
        .optional(),
    /**
     * 内容声明标注（可选），原样透传到发布体顶层 `tagInfo`
     *
     * 除 `tagType` 外的字段不做结构约束，调用方按页面行为自行组装并透传；
     * `tagKey` 由 SDK 在发布时向 finder_get_object_tag_list 实时获取后补进去，传了也会被覆盖
     * （它带时效，复用旧值会被服务端拒绝）。
     *
     * 已知需要附带额外字段的类型（详见 API.md）：
     * - tagType=5 内容为自行拍摄：`shootInfo: { postTimestamp, countryCode?, provinceCode?, cityCode? }`
     * - tagType=7 内容为转载：`repostSource: string`
     */
    tagInfo: z
        .looseObject({
            /** 标注类型，取值见 ShipinhaoDeclareTagTypes */
            tagType: z.number().int(),
        })
        .optional(),
    /** 是否原创声明（可选，对应顶层 originalFlag，默认 0=不声明） */
    originalFlag: z.union([z.literal(0), z.literal(1)]).optional(),
    /** 是否附带会员专区链接（可选，对应 objectDesc.member.postWithMemberZoneLink，默认 0） */
    postWithMemberZoneLink: z.union([z.literal(0), z.literal(1)]).optional(),
    /** 地理位置（可选） */
    location: z
        .object({
            latitude: z.number(),
            longitude: z.number(),
            city: z.string(),
            /** POI 名称，如「安徽医科大学第一附属医院」 */
            poiName: z.string().optional(),
            /** 详细地址，如「安徽省合肥市蜀山区绩溪路218号」 */
            address: z.string().optional(),
            poiClassifyId: z.string().optional(),
        })
        .optional(),
});

export type ShipinhaoPublishVideoParams = z.infer<typeof ShipinhaoPublishVideoParamsSchema>;

export type PublishVideoAction = CommonAction<ShipinhaoPublishVideoParams, string>;

export const shipinhaoPublishVideo: PublishVideoAction = async (task, params) => {
    task.logger.info(`[shipinhaoPublishVideo] actionType: ${params.actionType}`);

    if (params.actionType === "rpa") {
        return rpaAction(task, params);
    }

    if (params.actionType === "mockApi") {
        return mockAction(task, params);
    }

    return executeAction(mockAction, rpaAction)(task, params);
};
